import {
  type AskPunttSearchProgress,
  type AskPunttStructuredContent,
} from "@mg/schemas/src/christo/askPuntt";
import { ASK_PUNTT_WORKFLOW_GREETINGS } from "@mg/schemas/src/commons/enterprise";

/**
 * Mock data for Ask Puntt feature development
 */

// Workflow shortcut cards shown on the home page.
//
// `skillId` is the Knowledge-Base toggle key (matches enterprise
// `askPunttSkills[].skillId`); the home page filters cards by it. `workflowType`
// is the chat type the card opens. They differ for Shared Projects (toggled via
// `project_workflow`, opens a `project` chat). The long first-message greeting
// for each lives in `ASK_PUNTT_WORKFLOW_GREETINGS` (keyed by workflowType).
export const WORKFLOW_CARDS = [
  {
    id: "marketing_review",
    title: "Marketing Review",
    description:
      "Reviews ads, landing pages, social, and video against your rules",
    icon: "📣",
    skillId: "review" as const,
    workflowType: "review" as const,
  },
  {
    id: "artwork_review",
    title: "Artwork Review",
    description:
      "Compares artwork against label sheets, spec sheets, and rules",
    icon: "🎨",
    skillId: "artwork_review" as const,
    workflowType: "artwork_review" as const,
  },
  {
    id: "claims_intelligence",
    title: "Claims Intelligence",
    description:
      "Validates every claim against your science and market regulations",
    icon: "💡",
    skillId: "claim_generation" as const,
    workflowType: "claim_generation" as const,
  },
  {
    id: "shared_projects",
    title: "Shared Projects",
    description:
      "Marketing, project managers, creative, and regulatory in one place",
    icon: "👥",
    skillId: "project_workflow" as const,
    workflowType: "project" as const,
  },
  {
    id: "connectors",
    title: "Connectors",
    description: "Connects to 100+ tools you already use",
    icon: "🔌",
    skillId: "connectors" as const,
    workflowType: "connectors" as const,
  },
] as const;

// Mock search progress data (simulates the progressive search UI)
export const MOCK_SEARCH_SOURCES: AskPunttSearchProgress[] = [
  {
    source: "Puntt Regulatory Database",
    status: "done",
    resultCount: 12,
    results: [
      {
        title: "Dairy Market Standards",
        url: "eur-lex.europa.eu/eli/reg/2013/1308...",
      },
      {
        title: "Food: Probiotic Claims - ASA | CAP",
        url: "www.asa.org.uk",
      },
      {
        title: "Nutrition & Health Claims Regulation",
        url: "eur-lex.europa.eu/eli/reg/2006/192...",
      },
      {
        title:
          "Written questions and answers - Written questions, answers, and stateme...",
        url: "questions-statements.parliament.uk",
      },
    ],
  },
  {
    source: "Danone's Claims Data",
    status: "done",
    resultCount: 4,
    results: [
      {
        title: 'Approved claims, Activia: "With active probiotics"',
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
      {
        title: "Dairy Actimel Simple DComm Assets Risk Assessment",
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
      {
        title: "Compliance Database - Dairy & Yogurt",
        url: "danone.sharepoint.com/:f:/r/sites/...",
      },
    ],
  },
  {
    source: "Wrike",
    status: "done",
    resultCount: 24,
    results: [
      {
        title:
          "2120671 - Activia - Asda - Activia Gut Health Challenge - Asda : ACT-24291325",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "2033947 - Activia - Morrisons - Activia Gut Health Challenge - Morrisons : ACT-30590925",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "1932413 - IE - Activia Kefir Launch Materials - Activia - Digital Display;Print",
        url: "https://www.wrike.com/workspace...",
      },
      {
        title:
          "2055004 - Actimel - Actimel PMP KV - Booker;Bestway;BRP;Bidfood",
        url: "https://www.wrike.com/workspace...",
      },
    ],
  },
];

// Mock structured response matching the Figma compliance review design
export const MOCK_STRUCTURED_RESPONSE: AskPunttStructuredContent = {
  riskLevel: "medium",
  confidence: "high",
  shortAnswer:
    '"with active probiotics" is a non-compliant health claim. The word "probiotic" is the primary problem, and "active" makes it worse by emphasizing beneficial function.',
  regulatoryAnalysis: `**Primary issue: Implied Health Claim**

Regulation (EC) No 1924/2006 (Nutrition and Health Claims Regulation) prohibits unauthorized health claims:

- Article 10(1): Health claims require authorization and inclusion in the EU Register of permitted claims
- "Active probiotics" implies functional benefit (gut health, immune support, digestive function)
- The word "active" specifically suggests biological activity/health effect

**EU Register Check**

I searched the EU Register of Nutrition and Health Claims for authorized probiotic claims:

- Result: NO general "probiotic" or "active probiotic" claims are authorized
- Status: All yogurt/live culture health claims under evaluation or rejected since 2012 EFSA opinions
- Key finding: Even strain-specific probiotic claims (L. casei, Bifidobacterium) have been rejected for insufficient evidence`,
  alternatives: [
    {
      riskLevel: "low",
      claim: "Contains active probiotics",
      why: "Factual statement, no health claim",
      precedent: "Danone Activia (UK), Yakult (EU)",
      status: "Factual statement, no health implication",
      approvalPath: "Clear - describes product composition without claims",
      tradeOff: "Less impactful but no substantiation needed",
    },
    {
      riskLevel: "medium",
      claim: "May contribute to gut health*",
      why: "Qualified claim with disclaimer",
      precedent: "23 similar products in UK",
      status: "Compositional information only",
      approvalPath: "Immediate use",
      tradeOff: "Requires existing clinical data",
    },
  ],
  sources: [
    { label: "Puntt Regulatory Database", type: "regulatory" },
    { label: "Danone Sharepoint", type: "internal" },
    { label: "EUR Lex", url: "eur-lex.europa.eu", type: "regulatory" },
    { label: "ASA", url: "www.asa.org.uk", type: "regulatory" },
  ],
  timeSaved: {
    summary: "2-3 Weeks",
    breakdown: [
      { week: "Week 1", description: "Regulatory flags health claim" },
      { week: "Week 2", description: "Request for substantiation docs" },
      { week: "Week 3", description: "Iteration with legal team" },
    ],
  },
};

// Mock recent chats for sidebar
export const MOCK_RECENT_CHATS = [
  {
    _id: "chat-001",
    title: "Compliance Review for Active probiotics supp...",
    workflowType: "compliance_review" as const,
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-002",
    title: "2184369 - Cross-Brand - Celeb Retailer Jan He...",
    workflowType: "artwork_review" as const,
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-003",
    title: "Claim Generation - Positioning for...",
    workflowType: "claim_generation" as const,
    updatedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-004",
    title: "Brief Generation - Strategy - Positioning for",
    workflowType: "brief_creation" as const,
    updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    _id: "chat-005",
    title: "Compliance Review - Dairy Health Claims UK",
    workflowType: "compliance_review" as const,
    updatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  },
];

// Workflow type display names
const BUILTIN_WORKFLOW_TYPE_LABELS: Record<string, string> = {
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  artwork: "Artwork Review", // backward compat for old DB records
  compare: "Compare",
  review: "Marketing Review",
  brief_creation: "Brief Creation",
  claim_generation: "Claims Intelligence",
  project: "Shared Projects",
  connectors: "Connectors",
  general: "Chat",
};

export function getWorkflowTypeLabel(workflowType: string): string {
  return BUILTIN_WORKFLOW_TYPE_LABELS[workflowType] ?? workflowType;
}

// Keep for backwards compatibility
export const WORKFLOW_TYPE_LABELS = BUILTIN_WORKFLOW_TYPE_LABELS;

// Initial prompt messages per workflow type. The home page workflow cards now
// surface the canonical long greetings from `ASK_PUNTT_WORKFLOW_GREETINGS`
// (persisted server-side as the agent's first message); these remain as a
// client-side fallback for any workflow type without a shared greeting.
const BUILTIN_WORKFLOW_INITIAL_PROMPTS: Record<string, string> = {
  compliance_review:
    "Let's get started with your Compliance Review. What claim do you want to verify?",
  compare:
    "Let's compare your files. Upload the reference document and the deliverable you'd like reviewed.",
  brief_creation:
    "I'll help you create a compliant creative brief. What's the campaign or product?",
  general: "How can I help you today?",
};

export function getWorkflowInitialPrompt(workflowType: string): string {
  return (
    ASK_PUNTT_WORKFLOW_GREETINGS[workflowType] ??
    BUILTIN_WORKFLOW_INITIAL_PROMPTS[workflowType] ??
    "How can I help you today?"
  );
}

// Keep for backwards compatibility
export const WORKFLOW_INITIAL_PROMPTS = BUILTIN_WORKFLOW_INITIAL_PROMPTS;
